/**
 * Einstiege „Nachtragsangebot zu bestehendem Projekt" ausserhalb des Editors:
 * Angebotsliste („Neues Angebot" → Projektauswahl) und Projekt-Hub (Knopf bei
 * der Kunden-Verknüpfung). Beide legen über `createSupplementQuote` an.
 *
 * Rein, ohne React und ohne Supabase — die Komponenten reichen Rechte und
 * Suchtreffer herein.
 */
import type { RechtSchluessel } from '../../services/rechte/katalog';
import type { SupplementProjectOption } from '../../services/quoteSupplementService';

/** Wartezeit nach dem letzten Tastendruck, bevor die Projektsuche läuft. */
export const SUPPLEMENT_SEARCH_DELAY_MS = 250;

/**
 * Darf jemand aus dem Projekt heraus ein Nachtragsangebot anlegen? Admin oder
 * Recht „Angebote" — dieselbe Grenze wie das Anlegen in der Angebotsliste
 * (vertriebRechte.ts); die Datenbank prüft beim Insert ohnehin selbst.
 */
export const darfNachtragAnlegen = (
    isAdmin: boolean,
    hat: (recht: RechtSchluessel) => boolean,
): boolean => isAdmin || hat('angebote.verwalten');

/**
 * Zeigt das Projekt den Knopf „Nachtragsangebot anlegen"? Nur mit Recht, nicht
 * in der Nur-Ansicht, nur mit Sprungziel in die Angebote und nur für ein
 * gespeichertes Projekt (`temp-id` = Assistent, noch kein Datensatz).
 */
export const zeigeNachtragKnopf = (o: {
    darf: boolean;
    isReadOnly: boolean;
    hatSprung: boolean;
    projectId?: string | null;
}): boolean =>
    o.darf && !o.isReadOnly && o.hatSprung && Boolean(o.projectId) && o.projectId !== 'temp-id';

/** Darstellung eines Treffers in der Projektauswahl. */
export interface SupplementPickState {
    /** Wählbar? Gesperrte Projekte (Archiv, Tochter) bleiben ausgegraut sichtbar. */
    selectable: boolean;
    /** Kleine Zeile unter dem Projekt — Sperrgrund oder Hinweis. */
    note?: string;
    /** Sperrgrund (rot) statt Hinweis (grau). */
    blocked: boolean;
}

/**
 * Ein Treffer der Projektsuche: gesperrt mit Grund, oder wählbar — mit Hinweis,
 * wenn das Projekt nachweislich einem ANDEREN Kunden gehört als dem gewählten
 * (die Suche liefert nach den Projekten des Kunden auch fremde Treffer).
 * Fehlt die Kundenangabe am Projekt, gibt es keinen Hinweis: unbekannt ist
 * nicht dasselbe wie fremd.
 */
export const supplementPickState = (
    option: Pick<SupplementProjectOption, 'blocker' | 'customerId'>,
    customerId?: string | null,
): SupplementPickState => {
    const blocker = option.blocker?.trim();
    if (blocker) return { selectable: false, note: blocker, blocked: true };
    if (option.customerId && customerId && option.customerId !== customerId) {
        return { selectable: true, note: 'Projekt eines anderen Kunden', blocked: false };
    }
    return { selectable: true, blocked: false };
};
